import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Login from '../components/Login';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from || '/';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const fetchingStatesRef = useRef(false);
  const initialLoadRef = useRef(true);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '1', // Default to United States
    zipcode: '',
    password: '',
    confirmPassword: '',
    preferredContactMethod: 'Email'
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Fetch countries on component mount
  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (formData.country && !fetchingStatesRef.current) {
      fetchStatesByCountry(formData.country);
    } else if (!formData.country) {
      setStates([]);
    }
  }, [formData.country]);

  const fetchCountries = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      const response = await fetch(`${apiBaseUrl}/CommonChannel/GetCountries`);
      if (response.ok) {
        const data = await response.json();
        setCountries(data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStatesByCountry = async (countryId) => {
    if (fetchingStatesRef.current) return;
    
    fetchingStatesRef.current = true;
    try {
      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      const response = await fetch(`${apiBaseUrl}/CommonChannel/GetStatesByCountry?countryId=${countryId}`);
      if (response.ok) {
        const data = await response.json();
        setStates(data || []);
        
        // Set Georgia as default state only on initial load for United States
        if (countryId === '1' && initialLoadRef.current && data && data.length > 0) {
          const georgia = data.find(state => 
            (state.stateName && state.stateName.toLowerCase().includes('georgia')) ||
            (state.state_Name && state.state_Name.toLowerCase().includes('georgia'))
          );
          if (georgia) {
            const stateName = georgia.stateName || georgia.state_Name || '';
            setFormData(prev => ({
              ...prev,
              state: stateName
            }));
          }
          initialLoadRef.current = false;
        }
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      fetchingStatesRef.current = false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let sanitized = value;
    if (name === 'phone') sanitized = value.replace(/\D/g, '').slice(0, 20);
    if (name === 'zipcode') sanitized = value.replace(/[^A-Za-z0-9 \-]/g, '').slice(0, 10);
    
    // If country changes, reset state and clear stale zipcode error
    if (name === 'country') {
      setFormData(prev => ({
        ...prev,
        [name]: sanitized,
        state: ''
      }));
      setValidationErrors(prev => ({ ...prev, country: '', state: '', zipcode: '' }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: sanitized
      }));
      if (validationErrors[name]) {
        setValidationErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const validateField = (name, value, currentCountry = formData.country) => {
    let error = '';

    switch (name) {
      case 'firstName':
        if (!value.trim()) error = 'First name is required';
        else if (value.trim().length < 2) error = 'First name must be at least 2 characters';
        else if (value.trim().length > 30) error = 'First name must be 30 characters or less';
        break;
      
      case 'lastName':
        if (!value.trim()) error = 'Last name is required';
        else if (value.trim().length < 2) error = 'Last name must be at least 2 characters';
        else if (value.trim().length > 30) error = 'Last name must be 30 characters or less';
        break;
      
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
        if (!value.trim()) error = 'Email is required';
        else if (!emailRegex.test(value)) error = 'Please enter a valid email address (e.g. user@example.com)';
        break;
      
      case 'phone':
        // Enhanced phone validation - accepts various formats
        const phoneDigits = value.replace(/\D/g, '');
        if (!value.trim()) {
          error = 'Phone number is required';
        } else if (phoneDigits.length !== 10) {
          error = 'Phone number must be exactly 10 digits';
        } else if (!/^[2-9]\d{9}$/.test(phoneDigits)) {
          error = 'Please enter a valid 10-digit phone number';
        }
        break;
      
      case 'address1':
        if (!value.trim()) error = 'Address is required';
        else if (value.trim().length < 5) error = 'Please enter a complete address';
        else if (value.trim().length > 100) error = 'Address must be 100 characters or less';
        break;
      
      case 'city':
        // Enhanced city validation - only letters, spaces, hyphens
        if (!value.trim()) {
          error = 'City is required';
        } else if (value.trim().length < 2) {
          error = 'City name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s\-'.]+$/.test(value.trim())) {
          error = 'City name can only contain letters, spaces, hyphens, and apostrophes';
        } else if (value.trim().length > 50) {
          error = 'City name must be 50 characters or less';
        }
        break;
      
      case 'state':
        if (!value) error = 'State is required';
        break;
      
      case 'country':
        if (!value) error = 'Country is required';
        break;
      
      case 'zipcode': {
        if (!value.trim()) {
          error = 'Zip / Postal code is required';
        } else {
          const selCountry = countries.find(c => String(c.country_Id || c.countryId) === String(currentCountry));
          const cName = (selCountry?.country_Name || selCountry?.countryName || '').toLowerCase();
          if (cName.includes('canada')) {
            // Accept FSA only (H9W) or full postal code (H9W 5B5 / H9W5B5)
            if (!/^[A-Za-z]\d[A-Za-z]([ -]?\d[A-Za-z]\d)?$/.test(value.trim())) {
              error = 'Enter a valid Canadian postal code (e.g., H9W or H9W 5B5)';
            }
          } else if (cName.includes('united states') || cName.includes('u.s') || cName.includes('usa')) {
            const zipcodeDigits = value.replace(/\D/g, '');
            if (zipcodeDigits.length === 5) {
              if (!/^\d{5}$/.test(value.trim())) error = 'Zip code must be 5 digits (e.g., 30328)';
            } else if (zipcodeDigits.length === 9) {
              if (!/^\d{5}-?\d{4}$/.test(value.trim())) error = 'Zip code must be in format 12345 or 12345-6789';
            } else {
              error = 'Zip code must be 5 digits (e.g., 30328)';
            }
          }
          // All other countries: non-empty is sufficient
        }
        break;
      }
      
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 6) error = 'Password must be at least 6 characters';
        break;
      
      case 'confirmPassword':
        if (!value) error = 'Please confirm your password';
        else if (value !== formData.password) error = 'Passwords do not match';
        break;
      
      default:
        break;
    }

    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value, formData.country);
    
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    Object.keys(formData).forEach(key => {
      if (key !== 'address2' && key !== 'preferredContactMethod') {
        const error = validateField(key, formData[key]);
        if (error) {
          errors[key] = error;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setError('Please complete all required fields before continuing.');
      return;
    }

    setLoading(true);

    try {
      const apiBaseUrl = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';

      // Find country name from countries array
      const selectedCountry = countries.find(c => 
        String(c.country_Id || c.countryId) === String(formData.country)
      );
      const countryName = selectedCountry ? (selectedCountry.country_Name || selectedCountry.countryName) : '';

      const registrationData = {
        user_First_Name: formData.firstName,
        user_Last_Name: formData.lastName,
        user_Email: formData.email,
        user_Phone: formData.phone,
        user_Cell_Phone_No: formData.phone,
        user_Home_Phone_No: formData.phone,
        user_Address_Line1: formData.address1,
        user_Address_Line2: formData.address2 || '',
        user_City: formData.city,
        user_State: (formData.state || '').trim(),
        user_Country: countryName,
        userStateId: (formData.state || '').trim(),
        countryId: formData.country || '',
        user_Zipcode: formData.zipcode,
        user_Preferred_Contact_Method: formData.preferredContactMethod,
        user_Password: formData.password,
        createdBy: formData.email,
        created: new Date().toISOString(),
        modifiedBy: formData.email,
        modified: new Date().toISOString(),
        status: 'Y',
        // Required fields with default values
        stateSortCode: '',
        countrySortCode: '',
        user_Interested_In_Gurukul: 'N',
        user_Interested_In_Classes: 'Y',
        user_Other_State: '',
        user_Other_Country: '',
        is_Guest: 'N',
        baseUrl: '',
        apiKey: '',
        apiUserID: '',
        studentId: '',
        studentName: '',
        mode: '',
        customerProfileId: '',
        customerPaymentProfileId: '',
        hyc_customerProfileId: '',
        hyc_customerPaymentProfileId: '',
        rms_customerProfileId: '',
        rms_customerPaymentProfileId: ''
      };

      const response = await fetch(`${apiBaseUrl}/CommonChannel/InsertUserRegistration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          if (fromPath && fromPath !== '/') {
            // Came from class/camp page — go back and reopen login there
            navigate(fromPath, { state: { showLogin: true, registeredEmail: formData.email } });
          } else {
            // Came directly to /register — show login modal inline
            setSuccess(false);
            setShowLogin(true);
          }
        }, 2000);
      } else {
        let errorMsg = 'Registration failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorData.Message || errorMsg;
        } catch {}
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(`An error occurred during registration: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Successful!</h2>
          <p className="text-gray-600 mb-4">Your account has been created successfully.</p>
          <p className="text-sm text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        {/* Compact Registration Form */}
        <div className="bg-white rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.08)] border border-gray-100 p-6 relative">
          {/* Black border at top like login */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-black to-transparent"></div>
          
          {/* Compact Header inside form */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-black rounded-xl shadow-lg mb-2">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Create an Account</h1>
            <p className="text-xs text-gray-600">Join us today</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Personal Information Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* First Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={30}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all duration-200 text-sm ${
                    validationErrors.firstName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="First name"
                />
                {validationErrors.firstName && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={30}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.lastName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="Last name"
                />
                {validationErrors.lastName && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.lastName}</p>
                )}
              </div>

              </div>
            </div>

            {/* Contact Information Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                      validationErrors.email
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                    }`}
                    placeholder="email@example.com"
                  />
                  {validationErrors.email && (
                    <p className="text-red-500 text-xs mt-0.5">{validationErrors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={20}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.phone
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="10-digit phone number"
                />
                {validationErrors.phone && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.phone}</p>
                )}
              </div>

              </div>
            </div>

            {/* Address Information Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Address Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Address 1 */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="address1"
                    value={formData.address1}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    maxLength={100}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                      validationErrors.address1
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                    }`}
                    placeholder="Street address"
                  />
                  {validationErrors.address1 && (
                    <p className="text-red-500 text-xs mt-0.5">{validationErrors.address1}</p>
                  )}
                </div>

                {/* Address 2 */}
                <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Address 2
                </label>
                <input
                  type="text"
                  name="address2"
                  value={formData.address2}
                  onChange={handleInputChange}
                  maxLength={100}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-black focus:ring-1 focus:ring-gray-100 focus:outline-none transition text-sm"
                  placeholder="Apt, suite (optional)"
                />
              </div>

              {/* Country */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.country
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.country_Id || country.countryId} value={country.country_Id || country.countryId}>
                      {country.country_Name || country.countryName}
                    </option>
                  ))}
                </select>
                {validationErrors.country && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.country}</p>
                )}
              </div>

              {/* State */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  disabled={!formData.country || states.length === 0}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.state
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                >
                  <option value="">Select State</option>
                  {states.map((state, index) => {
                    const sName = (state.stateName || state.state_Name || '').trim();
                    return (
                      <option key={index} value={sName}>{sName}</option>
                    );
                  })}
                </select>
                {validationErrors.state && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.state}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={50}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.city
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="City"
                />
                {validationErrors.city && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.city}</p>
                )}
              </div>

              {/* Zipcode */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {(() => {
                    const sc = countries.find(c => String(c.country_Id || c.countryId) === String(formData.country));
                    const cn = (sc?.country_Name || sc?.countryName || '').toLowerCase();
                    if (cn.includes('canada')) return <>Postal Code <span className="text-red-500">*</span></>;
                    if (cn.includes('united states') || cn.includes('usa')) return <>Zip Code <span className="text-red-500">*</span></>;
                    return <>Postal / Zip Code <span className="text-red-500">*</span></>;
                  })()}
                </label>
                <input
                  type="text"
                  name="zipcode"
                  value={formData.zipcode}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={10}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.zipcode
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder={(() => {
                    const sc = countries.find(c => String(c.country_Id || c.countryId) === String(formData.country));
                    const cn = (sc?.country_Name || sc?.countryName || '').toLowerCase();
                    if (cn.includes('canada')) return 'L8G 3X4';
                    if (cn.includes('united states') || cn.includes('usa')) return '12345';
                    return 'Postal / Zip code';
                  })()}
                />
                {validationErrors.zipcode && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.zipcode}</p>
                )}
              </div>

              </div>
            </div>

            {/* Security Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Security
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                      validationErrors.password
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                    }`}
                    placeholder="Password"
                  />
                  {validationErrors.password && (
                    <p className="text-red-500 text-xs mt-0.5">{validationErrors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.confirmPassword
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="Confirm password"
                />
                {validationErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Submit Buttons - Minimalist */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowLogin(true)}
                disabled={loading}
                className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-black text-white font-bold py-3 px-6 rounded-xl hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            {/* Login Link */}
            <div className="text-center pt-2 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setShowLogin(true)}
                  className="text-black hover:underline font-semibold"
                >
                  Login here
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <Login
          onLoginSuccess={(data) => {
            setShowLogin(false);
            navigate('/');
          }}
          onCancel={() => setShowLogin(false)}
          entity="RMS"
        />
      )}
    </div>
  );
};

export default Register;
